/** The quote's visit card on the engine's shells (moved from `quote/quote-rows.tsx` at step 8). */
import { type MouseEvent, type ReactNode } from 'react'
import { VISIT_ROLE_LABELS, type QuoteVisitView, type VisitRole } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { LineGridScope, type QtyMode } from './grid.js'
import { LineHeader, type LineHeaderExample } from './line-header.js'
import { VisitShell } from './visit-shell.js'
import { formatDollars, formatNumber, formatRate } from '../../lib/format.js'
import { IconCall, IconCalendar, IconEye, IconFollows } from '../../frame/icons.js'
import { visitKindGlyph } from '../fields/kind-field.js'
import { OPTIONAL_LABEL } from '../copy.js'
import { ChoiceChips } from '../fields/choices-field.js'
import { VisitMarginChip } from '../margin/visit-margin.js'
import { QtyInput } from './line-row.js'
import { IconMoney, IconNoteLines, IconRepeat, IconVisit } from './glyphs.js'

// ---------------------------------------------------------------------------
// The visit
// ---------------------------------------------------------------------------

export interface VisitCardProps {
  visit: QuoteVisitView
  /** Drawn inside; the card only knows it is holding services. */
  children: ReactNode
  selected: boolean
  collapsed: boolean
  onSelect: () => void
  onToggle: () => void
  onEditName: () => void
  onEditSchedule: () => void
  onEditBilling: () => void
  onEditNotes: () => void
  /**
   * **On the proposal** (`docs/proposal.md` §10.5) — what the customer reads
   * for this visit. Given the element so the popover can hang off it.
   */
  onEditProposal: (anchor: HTMLElement) => void
  onQty: (qty: number) => void
  /** Given the link itself, so the picker can hang off it. */
  onAddService: (anchor: HTMLElement) => void
  onMenu: (at: { x: number; y: number }) => void
  /**
   * A quote that is not a draft (walkthrough findings §4, folder 52): the
   * trip count and the facts open nothing, *+ Add service* and the ⋯ are
   * absent.
   */
  readOnly?: boolean
  targetMarginPct: number
  /** Drawn after the auto-named tag: "from package", on a generated visit. */
  chip?: ReactNode
  /** The margin chip opens the visit's margin popover (folder 44 §7). */
  marginOpen?: boolean
  onMargin?: (anchor: HTMLElement) => void
  /** The choices this visit is in, by name; a chip opens its Choices field (§9.1). */
  choices?: string[]
  onChoices?: (event: MouseEvent<HTMLElement>) => void
  /** The adapter's quantity cell — a package's lines are rules (§3 row 1). */
  qtyMode?: QtyMode
  /**
   * A package's visit (Board 5) rather than a quote's. It has no quote to
   * follow, so the fourth fact is drawn only once somebody has changed what
   * the customer reads; and a trip with no labor prints no `0 h / visit`.
   * Defaults to the quantity cell, because a rule there is what a package is.
   */
  recipe?: boolean
}

/** Has anybody overridden what the customer reads for this visit? */
const customised = (visit: QuoteVisitView): boolean =>
  visit.proposal.timing !== null || visit.proposal.priceView !== null
  || visit.proposal.detail !== null || visit.proposal.phraseId !== null
  || visit.proposal.phraseText !== null

/**
 * A quote's visit, on the shared shell.
 *
 * Everything this adds is quote data: how many trips the schedule says, what
 * the money is, and the three facts under the name that each open their own
 * part of the editor. The card, the tile, the collapse and the ⋯ are
 * `VisitShell`, which the package builder draws too.
 */
export function VisitCard({
  visit, children, selected, collapsed,
  onSelect, onToggle, onEditName, onEditSchedule, onEditBilling, onEditNotes,
  onEditProposal,
  onQty, onAddService, onMenu, readOnly = false, targetMarginPct, chip, marginOpen = false, onMargin,
  choices = [], onChoices, qtyMode = 'number', recipe = qtyMode === 'rule',
}: VisitCardProps) {
  /*
   * An open-ended visit's `occurrences` arrives as null — `Infinity` does not
   * survive JSON — so the division has to be guarded rather than trusted. Its
   * hours are one trip's, which is the honest number to show beside a count
   * that is "∞".
   */
  const trips = Number.isFinite(visit.occurrences) ? visit.occurrences : null
  const perVisit = trips && trips > 0 ? visit.hours / trips : visit.hours

  return (
    <VisitShell
      /*
       * What kind of trip this is, in the tile. A driver dropping four
       * dumpsters and a crew mowing are both "visits" and the card should not
       * claim they are the same errand.
       */
      tile={<VisitTile role={visit.role} />}
      name={visit.name}
      onEditName={readOnly ? undefined : onEditName}
      tags={(
        <>
          {/* The kind, as a light chip after the name (visits §5.2, Board 10). */}
          <VisitTag>{VISIT_ROLE_LABELS[visit.role]}</VisitTag>
          {visit.autoNamed && <VisitTag>auto-named</VisitTag>}
          {chip}
          {visit.optional && <VisitTag tone="info">{OPTIONAL_LABEL}</VisitTag>}
          <ChoiceChips names={choices} onOpen={onChoices} />
        </>
      )}
      /*
       * Three facts, each a button onto the part of the editor that owns it.
       * Clicking "Weekly · Apr 6 – Oct 26" should open the schedule, not a
       * dialog with four tabs where one of them is the schedule.
       */
      meta={(
        <>
          {/*
            * The When, in `visitWhenPhrase()`'s words — the same sentence the
            * popover's Reads line and the connector chip print (§4.4) — with
            * the glyph that says what kind of answer it is (§5.2).
            */}
          <MetaButton onClick={onEditSchedule} icon={whenGlyph(visit)} disabled={readOnly}>
            {visit.phrase.card}
          </MetaButton>
          <MetaButton onClick={onEditBilling} icon={<IconMoney size={13} />} disabled={readOnly}>
            {visit.billingPlanName ?? 'No billing plan'}
          </MetaButton>
          <MetaButton onClick={onEditNotes} icon={<IconNoteLines size={13} />} disabled={readOnly}>
            {noteSummary(visit)}
          </MetaButton>
          {/*
            * The fourth fact, and the only one about the *document* rather
            * than the work: what this visit reads like to the customer. It
            * says "Follows the quote" until somebody overrides something,
            * because that is what is true for almost every visit and a
            * button that always says "On the proposal" tells nobody whether
            * it has been touched (§10.5).
            */}
          {/*
            * A package visit has no quote to follow (Board 5 draws three
            * facts), so it says something only when there is something to
            * say — an override written on the package.
            */}
          {(!recipe || customised(visit)) && (
            <MetaButton
              onClick={(event) => onEditProposal(event.currentTarget)}
              icon={<IconEye size={13} />}
              disabled={readOnly}
            >
              {customised(visit) ? 'On the proposal · changed' : 'Follows the quote'}
            </MetaButton>
          )}
        </>
      )}
      /*
       * How many trips this is — `occurrences`, not `qty`. They are the same
       * number whenever somebody typed it, and when a season and a frequency
       * worked it out `qty` is just the last thing anyone typed: the card said
       * × 1 beside a weekly visit that priced as thirty.
       *
       * And when the schedule is deciding, this is a read-out rather than a
       * box. Typing 20 over a number the dates say is 30 is an edit the next
       * recalculation throws away, so it opens the schedule instead — the
       * place where the answer actually lives.
       */
      qty={visit.openEnded
        ? (
          /*
           * However many trips the customer keeps the units for. Nothing on
           * the quote knows that number, so the card says so rather than
           * printing a one somebody would read as a price.
           */
          <TripCount
            onClick={onEditSchedule}
            disabled={readOnly}
            title="Runs until the units come back. Click to change how often."
          >
            <b className="text-[14px] font-semibold text-text">∞</b>
          </TripCount>
        )
        : visit.countScheduled
          ? (
            <TripCount
              onClick={onEditSchedule}
              disabled={readOnly}
              title="Set by the schedule. Click to change the dates or how often."
            >
              <b className="tnum text-[13px] font-semibold text-text">{trips}</b>
            </TripCount>
          )
          : (
            <label className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-body',
              'text-text-muted')}>
              ×
              <QtyInput
                value={trips ?? 1}
                onCommit={onQty}
                disabled={readOnly}
                label={`How many ${visit.name} visits`}
                width="w-14"
                className="justify-center font-semibold"
                step={1}
              />
              visits
            </label>
          )}
      right={(
        <span className="flex flex-col items-end gap-px">
          <b className="tnum text-[16px] font-bold text-text">{formatDollars(visit.total)}</b>
          {/*
            * The visit's own figure from the server, as a chip that opens the
            * popover (§7.1). It used to be worked out here from total and
            * cost — a second opinion about a number `quoteMargin()` owns.
            */}
          <span className="flex items-center gap-1.5 whitespace-nowrap text-[11px] text-text-muted">
            {/* A package trip whose only line is a fee has no hours to print (Board 5). */}
            {(!recipe || perVisit > 0) && <>{formatNumber(Math.round(perVisit * 10) / 10)} h / visit</>}
            <VisitMarginChip
              pct={visit.marginPct}
              targetPct={targetMarginPct}
              open={marginOpen}
              onOpen={(anchor) => onMargin?.(anchor)}
              label={`${visit.name} margin`}
            />
          </span>
        </span>
      )}
      selected={selected}
      collapsed={collapsed}
      summary={collapsedSummary(visit)}
      onSelect={onSelect}
      onToggle={onToggle}
      onMenu={readOnly ? undefined : onMenu}
      footer={readOnly ? undefined : (
        <>
          <button
            type="button"
            onClick={(event) => onAddService(event.currentTarget)}
            className="text-body font-semibold text-accent hover:text-accent-dark"
          >
            + Add service
          </button>
          <span className="text-body text-text-faint">from your catalog</span>
        </>
      )}
    >
      {/*
        * One header strip per visit, directly above its first service, and
        * the same grid every row below it reads (folder 44 §5.2).
        */}
      <LineGridScope targetPct={targetMarginPct} qty={qtyMode}>
        {visit.services.length > 0 && <LineHeader example={headerExample(visit, targetMarginPct)} />}
        {children}
      </LineGridScope>
    </VisitShell>
  )
}

/**
 * The header tips' worked examples, from the first line of this visit — so
 * the example is always a line on screen (§5.3). Every figure is the
 * server's; nothing here multiplies or subtracts.
 */
function headerExample(visit: QuoteVisitView, targetPct: number): LineHeaderExample | null {
  const line = visit.services.flatMap((service) => service.lines)[0]
  if (!line) return null
  const qty = formatNumber(line.qty)
  const under = line.marginPct !== null && line.marginPct < targetPct
  return {
    qty: `${qty} ${line.unit}`,
    priceEach: `${formatRate(line.rate)} per ${line.unit}`,
    price: <>{qty} × {formatRate(line.rate)} = <b className="text-text">{formatRate(line.amount)}</b></>,
    cost: line.costEach === null
      ? <b className="text-text">{formatRate(line.cost)}</b>
      : <>{qty} × {formatRate(line.costEach)} = <b className="text-text">{formatRate(line.cost)}</b></>,
    margin: line.marginPct === null
      ? '—'
      : (
        <>
          {formatRate(line.gross)} ÷ {formatRate(line.amount)} ={' '}
          <b className={under ? 'text-warn' : 'text-text'}>{line.marginPct.toFixed(1)}%</b>
        </>
      ),
  }
}

/**
 * The 40px visit tile — `design-system.md` §4.2.
 *
 * `accent-soft` behind a 1.5px `accent-line` border, and the glyph says what
 * sort of trip it is. Exported for the package builder, whose visits are the
 * same four roles.
 */
export function VisitTile({ role }: { role?: VisitRole }) {
  return (
    <span
      aria-hidden
      className={cn('flex h-[40px] w-[40px] shrink-0 items-center justify-center',
        'rounded-tile-large border-[1.5px] border-accent-line bg-accent-soft text-accent')}
    >
      {role ? visitKindGlyph(role, 18) : <IconVisit size={18} />}
    </span>
  )
}

/** The meta line's glyph: ↳ after another visit, ↻ for a repeat, a phone when called, else a day. */
function whenGlyph(visit: QuoteVisitView): ReactNode {
  const { starts, repeats } = visit.when
  if (starts.kind === 'after') return <IconFollows size={13} />
  if (starts.kind === 'on_call') return <IconCall size={13} />
  if (repeats.kind === 'every') return <IconRepeat size={13} />
  return <IconCalendar size={13} />
}

/**
 * The connector between two cards when the lower follows the upper (visits
 * §5.2, Board 10): a 30px row, a 2px rule down the tiles' line and a 22px info
 * chip saying the follower's `card` phrase. Drawn only between neighbours —
 * anywhere else the card's own meta line says it alone.
 *
 * It sits in the 14px gap the list already has, so it pulls in by that much
 * either side: the cards end up 30px apart, as drawn.
 */
export function VisitConnector({ phrase }: { phrase: string }) {
  return (
    <div data-visit-connector="" className="relative -my-3.5 flex h-[30px] items-center pl-[34px]">
      <i aria-hidden className="absolute bottom-[-6px] left-[41px] top-[-6px] w-0.5 bg-info/30" />
      <span className="relative ml-[22px] inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-[7px] bg-info-bg px-[9px] text-[11.5px] font-semibold text-info">
        <IconFollows size={12} />
        {phrase}
      </span>
    </div>
  )
}

/** Whether `visit` follows the one drawn just above it — the only place a connector goes. */
export function followsAbove(visit: QuoteVisitView, above: QuoteVisitView | undefined): boolean {
  return Boolean(above && visit.when.starts.kind === 'after' && visit.when.starts.anchorId === above.id)
}

/** "× 12 visits" as a button — the count the schedule owns. */
function TripCount({ onClick, title, children, disabled = false }: {
  onClick: () => void; title: string; children: ReactNode; disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? undefined : title}
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-field px-1.5',
        'py-1 text-body text-text-muted enabled:hover:bg-hover')}
    >
      ×
      {children}
      visits
    </button>
  )
}

/** "N services · names · hours" — what a collapsed visit still has to say. */
function collapsedSummary(visit: QuoteVisitView): string {
  const names = visit.services.map((service) => service.name).join(' · ')
  const count = `${visit.services.length} service${visit.services.length === 1 ? '' : 's'}`
  const hours = visit.hours > 0 ? ` · ${formatNumber(visit.hours)} h` : ''
  return names ? `${count} · ${names}${hours}` : `${count}${hours}`
}

/** Which audiences have notes, said in the fewest words that are still true. */
function noteSummary(visit: QuoteVisitView): string {
  const crew = visit.noteCrew.trim()
  const audiences = [
    visit.noteQuote.trim() && 'Quote',
    visit.noteInternal.trim() && 'Internal',
    crew && 'crew',
  ].filter(Boolean)

  if (audiences.length === 0) return 'No notes'
  // The crew note is the one with consequences, so it gets quoted verbatim.
  if (crew) return `Crew: ${crew.length > 28 ? `${crew.slice(0, 28)}…` : crew}`
  return audiences.join(' + ')
}

function MetaButton({
  icon, children, onClick, disabled = false,
}: {
  icon: ReactNode
  children: ReactNode
  /** The event, because one of these has to hang a popover off its button. */
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** A quote that is not a draft: the fact is read, not opened (folder 52). */
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn('inline-flex min-w-0 items-center gap-1.5', !disabled && 'hover:text-text')}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </button>
  )
}

export function VisitTag({
  children, tone = 'plain',
}: {
  children: ReactNode
  /** `accent` is a choice the office recommends (step 22 item 2.5) — the green pair, same chip. */
  tone?: 'plain' | 'info' | 'choice' | 'accent'
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[19px] shrink-0 items-center whitespace-nowrap rounded-tag px-1.5',
        'text-[11px] font-semibold',
        tone === 'plain' && 'bg-line-soft text-text-muted',
        tone === 'info' && 'bg-info-bg text-info',
        tone === 'accent' && 'bg-accent-soft text-accent-dark',
      )}
      style={tone === 'choice'
        ? {
          background: 'rgb(var(--status-quoteLine-deployment-bg))',
          color: 'rgb(var(--status-quoteLine-deployment-fg))',
        }
        : undefined}
    >
      {children}
    </span>
  )
}
